<template>
  <div v-if="readPermission" class="tenantManagementContainer">
    <div class="configurePageContainer custom-extraction-field-top-bar" style="width: 100%; height: 250px">
      <p class="extraction-top-bar-title" style="text-align: left">{{ $t('extractionFields.selection_instruction') }}</p>

      <div class="dropdown-container">
        <div class="dropdown-grid">
          <div class="form-field">
            <label>{{ $t('extractionFields.forms.labels.tenant') }}</label>
            <select v-model="selectedValues.tenantId" class="custom-select">
              <option value="">{{ $t('extractionFields.forms.placeholders.select_tenant') }}</option>
              <option v-for="tenant in tenants" :key="tenant.value" :value="tenant.value">
                {{ tenant.label }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label>{{ $t('extractionFields.forms.labels.primary_classification') }}</label>
            <select v-model="selectedValues.categoryId" class="custom-select">
              <option value="">{{ $t('extractionFields.forms.placeholders.select_primary_classification') }}</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.category_name }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label>{{ $t('extractionFields.forms.labels.sub_category') }}</label>
            <select v-model="selectedValues.departmentId" class="custom-select">
              <option value="">{{ $t('extractionFields.forms.placeholders.select_sub_category') }}</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                {{ dept.department_name }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label>{{ $t('extractionFields.forms.labels.document_type') }}</label>
            <select v-model="selectedValues.documentTypeId" class="custom-select">
              <option value="">{{ $t('extractionFields.forms.placeholders.select_document_type') }}</option>
              <option v-for="type in documentTypes" :key="type.id" :value="type.id">
                {{ type.document_type_name }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label>{{ $t('extractionFields.forms.labels.vasion_form') }}</label>
            <select v-model="selectedValues.vasionFormId" class="custom-select" :disabled="true">
              <option value="">{{ $t('extractionFields.forms.placeholders.select_vasion_form') }}</option>
              <option v-for="form in vasionForms" :key="form.id" :value="form.id">
                {{ form.vasion_form_id }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="configurePageContainer" style="height: 100%; width: 100%">
      <div v-if="loading" style="position: absolute; top: 50%; left: 65%; transform: translate(-50%, -50%); display: flex; justify-content: center; align-items: center">
        <div class="spinner-border text-primary spinner-grow-xl" roleCategories="status"></div>
      </div>
      <div class="customRowTenant">
        <p class="jobHistoryTitle" style="text-align: left">{{ $t('extractionFields.title') }}</p>
        <div class="searchBar">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
            <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <input type="text" class="searchInput" :placeholder="$t('extractionFields.search_placeholder')" v-model="search" @input="handleSearch" />
        </div>
      </div>

      <!-- Search & Add Button -->
      <div class="tableHeadingsNew" style="display: flex; align-items: center; margin-top: 20px; gap: 16px">
        <!-- Pagination Controls -->
        <div class="customRow" style="display: flex; align-items: center; justify-content: start; gap: 16px; width: 100%">
          <img src="/backward.svg" alt="Back" style="cursor: pointer" @click="goToPreviousPage" />
          <span class="pageOfTitle">{{ $t('extractionFields.pagination.page') }} {{ page }} {{ $t('extractionFields.pagination.of') }} {{ totalPages }}</span>
          <img src="/forward.svg" alt="Next" style="cursor: pointer" @click="goToNextPage" />

          <div class="pageDiv" @click.stop="togglePageSizeDropdown">
            <span class="pageDivText">{{ pageSize }}</span>
            <img src="/arrowDownNew.png" class="arrowDownNew" alt="" />
            <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdownNew" @click.stop>
              <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                {{ size }}
              </li>
            </ul>
          </div>
        </div>

        <!-- AI Method Selector and Sync Buttons -->
        <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 6px; width: 100%">
          <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 6px; width: 100%">
            <div style="display: flex; align-items: center; justify-content: flex-end; gap: 12px; width: 100%">
              <label style="font-weight: 500; min-width: 90px; text-align: right">{{ $t('extractionFields.ai_method_label') }}</label>
              <span style="width: 70%; padding: 6px 12px; background: #f5f5f5; border: 1px solid #ccc; border-radius: 4px">
                {{ selectedAiMethod || $t('extractionFields.forms.placeholders.select_tenant') }}
              </span>
            </div>
          </div>

          <div v-if="selectedValues.tenantId && userRole === 'Super Admin'" style="font-size: 13px; color: #8f969b; text-align: right; margin-right: 8px">
            {{ $t('extractionFields.configure_ai_method') }}
            <span @click="navigateToEdit({ id: selectedValues.tenantId })" style="color: var(--primary-color); text-decoration: underline; cursor: pointer"> {{ $t('extractionFields.tenant_details') }} </span>
          </div>
        </div>
      </div>

      <!-- Extraction Fields Table -->
      <div class="tableContainer" style="margin-top: 20px">
        <table class="customTable" style="font-size: 13px">
          <thead class="customTableHeader">
            <tr class="customTableRow">
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.sr_no') }}</th>
              <th style="font-size: 13px">
                <div style="display: flex; align-items: center; gap: 6px">
                  <span>{{ $t('extractionFields.table.headers.vasion_field') }}</span>
                  <i class="bi bi-arrow-clockwise sync-icon-header" :title="$t('extractionFields.table.sync_tooltips.sync_vasion_fields')" @click="handleSyncNewFields"></i>
                </div>
              </th>
              <!-- <th style="width: 120px; font-size: 13px">{{ $t('extractionFields.table.headers.vasion_field_key') }}</th> -->
              <th style="width: 100px; font-size: 13px">{{ $t('extractionFields.table.headers.data_type') }}</th>
              <th style="font-size: 13px">
                <div style="display: flex; align-items: center; gap: 6px">
                  <span>{{ aiMethodToDisplay || $t('extractionFields.table.headers.ai_method_fields') }}</span>
                  <i class="bi bi-arrow-clockwise sync-icon-header" :title="$t('extractionFields.table.sync_tooltips.sync_ai_method_fields')" @click="handleSyncAiMethodFields"></i>
                </div>
              </th>
              <th style="width: 120px; font-size: 13px">{{ $t('extractionFields.table.headers.default_value_required') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.length') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.confidence_threshold') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.required_for_vasion') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.is_active') }}</th>
            </tr>
          </thead>

          <tbody class="customTableBody">
            <tr v-for="(fields, index) in extractionFields" :key="fields.id" style="height: 50px !important; font-size: 13px">
              <td style="font-size: 13px">{{ index + 1 + (page - 1) * pageSize }}</td>
              <td style="font-size: 13px">{{ fields.vasion_field_name }}</td>
              <!-- <td style="font-size: 13px">
                <div class="vasion-field-key-container">
                  <span class="vasion-field-key-text" :class="{ 'vasion-field-key-truncated': fields?.vasion_field_key && fields.vasion_field_key.length > 20 }" @mouseenter="showTooltip($event, fields?.vasion_field_key)" @mouseleave="hideTooltip()">
                    {{ truncateText(fields?.vasion_field_key, 16) }}
                  </span>
                  <div v-if="tooltipVisible && tooltipText" class="vasion-field-key-tooltip" :style="tooltipStyle">
                    {{ tooltipText }}
                  </div>
                </div>
              </td> -->
              <td style="font-size: 13px">
                {{ fields.vasion_field_data_type }}
                <!-- {{ fields.vasion_field_data_type === 'Dropdown' ? ':' : '' }} -->

                <div v-if="fields.vasion_field_data_type === 'Dropdown' && fields.vasion_drop_down_values" class="dropdown-values-container">
                  <!-- <span class="dropdown-values-text" :class="{ 'dropdown-values-truncated': getFormattedDropdownValues(fields.vasion_drop_down_values).length > 25 }" @mouseenter="showDropdownTooltip($event, fields.vasion_drop_down_values)" @mouseleave="hideDropdownTooltip()">
                    {{ truncateText(getFormattedDropdownValues(fields.vasion_drop_down_values), 25) }}
                  </span> -->
                  <button class="dropdown-view-icon" @click="openDropdownModal(fields)" :title="$t('extractionFields.view_all_dropdown_values')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="#7AC4EB" stroke-width="2" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5C16.478 5 20.268 7.943 21.542 12C20.268 16.057 16.478 19 12 19C7.523 19 3.732 16.057 2.458 12Z" stroke="#7AC4EB" stroke-width="2" />
                    </svg>
                  </button>
                  <div v-if="dropdownTooltipVisible && dropdownTooltipText" class="dropdown-values-tooltip" :style="dropdownTooltipStyle">
                    {{ dropdownTooltipText }}
                  </div>
                </div>
                <!-- <span v-else-if="fields.vasion_field_data_type === 'Dropdown'">-</span>
                <span v-else>-</span> -->
              </td>

              <!-- AI Method Field Selection Column -->
              <td v-if="this.selectedAiMethod == 'Custom Comprehend'" style="font-size: 13px">
                <span v-if="isSystemField(fields.vasion_field_name)" style="color: #8f969b; font-style: italic">
                  {{ $t('extractionFields.system_field') || 'System Field' }}
                </span>
                <select v-else v-model="fields.document_field_id" @change="handleDocumentFieldSelect(fields)" class="tenant-input" style="font-size: 13px">
                  <option :value="null">{{ $t('extractionFields.forms.placeholders.select_field') }}</option>
                  <option v-for="option in aiMethodFieldsMap[selectedAiMethod]" :key="option.id" :value="option.id">{{ option.field_label }}</option>
                </select>
              </td>
              <td v-else style="font-size: 13px">
                <span v-if="isSystemField(fields.vasion_field_name)" style="color: #8f969b; font-style: italic">
                  {{ $t('extractionFields.system_field') || 'System Field' }}
                </span>
                <select v-else v-model="fields[this.selectedAiMethod === 'Bedrock Data Automation' ? 'bedrock_field' : 'vasion_field_key']" @change="handleAiFieldSelect(fields)" class="tenant-input" style="font-size: 13px">
                  <option :value="null">{{ $t('extractionFields.forms.placeholders.select_field') }}</option>
                  <option v-for="option in getAiMethodFields()" :key="option" :value="option">{{ option }}</option>
                </select>
              </td>
              <td style="font-size: 13px; text-align: center">
                <input v-if="fields.vasion_field_data_type === 'Dropdown' && fields.vasion_drop_down_values" type="checkbox" :checked="fields.default_value_required" style="opacity: 0.8; font-size: 13px" @click.prevent="(event) => handleDefaultCheck(fields, !fields.default_value_required)" />
              </td>
              <td style="font-size: 13px">{{ fields.vasion_field_length }}</td>
              <td style="font-size: 13px">
                <input type="number" step="0.01" min="0" max="100" :value="fields.ml_confidence_level" @change="handleConfidenceChange($event, fields)" class="confidence-input" pattern="[0-9]*\.?[0-9]*" style="font-size: 13px" />
              </td>
              <td style="font-size: 13px">
                <input type="checkbox" :checked="fields.vasion_required" style="cursor: not-allowed; opacity: 0.8; font-size: 13px" @click.prevent />
              </td>
              <td style="font-size: 13px">
                <div class="toggle-wrapper">
                  <CustomToggle :modelValue="fields.is_active" @update:modelValue="(value) => handleStatusToggle(fields, value)" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <ConfirmationModal :show="showConfirmModal" :message="$t('extractionFields.modals.confirm_status.message', { fieldName: pendingStatusChange?.field?.vasion_field_name })" @close="closeConfirmModal" @confirm="confirmStatusChange" />
    <ConfirmationModal :show="showConfirmModalForDefault" :message="$t('extractionFields.modals.confirm_default_value.message', { fieldName: pendingStatusChange?.field?.vasion_field_name })" @close="closeConfirmModal" @confirm="confirmDefaultValueChange" />
    <DropdownValuesModal :show="showDropdownModal" :dropdownValues="selectedDropdownValues" :fieldName="selectedFieldName" @close="closeDropdownModal" />
  </div>
  <div v-else>
    <PermissionAlert />
  </div>
</template>

<script>
import { get, post, put } from '@/utils/api'
import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import { showToast } from '@/utils/toast'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import DropdownValuesModal from '@/components/DropdownValuesModal/DropdownValuesModal.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
import tenantSelectionStore from '@/stores/tenantSelectionStore'
export default {
  components: {
    CustomToggle,
    ConfirmationModal,
    DropdownValuesModal,
    PermissionAlert,
  },
  computed: {
    loading() {
      return Object.values(this.loadingStates).some((state) => state)
    },
  },
  data() {
    return {
      userRole: localStorage.getItem('userRole'),
      readPermissionMaster: false,
      writePermissionMaster: false,
      readPermission: false,
      writePermission: false,
      loadingStates: {
        tenants: false,
        updateExtractionField: false,
        departments: false,
        vasionForms: false,
        tenantCategories: false,
        documentTypes: false,
        extractionFields: false,
        syncVasionFields: false,
      },
      aiMethodFieldsMap: {
        'Default Textract': [],
        'Custom Comprehend': [],
        'Bedrock Data Automation': [],
      },
      selectedAiMethod: '', // You can bind this from tenant config if needed
      departments: [],
      categories: [], // To store categories for dropdown
      tenants: [],
      filters: null,
      documentTypes: [],
      vasionForms: [],
      search: '',
      debounceTimeout: null,
      extractionFields: [],
      page: 1,
      pageSize: 10,
      total: 0,
      aiMethodToDisplay: '',
      totalPages: 1,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      newDepartment: {
        categoryId: '',
        name: '',
        isActive: true,
      },
      selectedValues: {
        tenantId: '',
        categoryId: '',
        departmentId: '',
        documentTypeId: '',
        vasionFormId: '',
      },
      tenantStoreUnsubscribe: null,
      selectedDocType: null,
      showConfirmModal: false,
      showConfirmModalForDefault: false,
      pendingStatusChange: null,
      // Tooltip data for vasion field key
      tooltipVisible: false,
      tooltipText: '',
      tooltipStyle: {},
      // Tooltip data for dropdown values
      dropdownTooltipVisible: false,
      dropdownTooltipText: '',
      dropdownTooltipStyle: {},
      // Dropdown modal data
      showDropdownModal: false,
      selectedDropdownValues: [],
      selectedFieldName: '',
    }
  },
  mounted() {
    document.title = this.$t('extractionFields.title')
    this.checkPermissions()

    // Initialize with current store state FIRST
    const currentState = tenantSelectionStore.getState()
    console.log('ExtractionFields: Initializing with store state:', currentState)
    this._isStoreUpdate = true
    this.selectedValues.tenantId = currentState.selectedTenantId
    this._isStoreUpdate = false

    // Subscribe to tenant selection store
    this.tenantStoreUnsubscribe = tenantSelectionStore.subscribe((state) => {
      const previousTenantId = this.selectedValues.tenantId
      console.log('ExtractionFields: Store update received:', state, 'current:', this.selectedValues.tenantId)

      // Set flag to prevent watcher from triggering store update
      this._isStoreUpdate = true
      this.selectedValues.tenantId = state.selectedTenantId
      this._isStoreUpdate = false

      console.log('ExtractionFields: Updated tenantId to:', this.selectedValues.tenantId)

      // Update AI method when tenant changes
      if (previousTenantId !== state.selectedTenantId && state.selectedTenantId) {
        console.log('ExtractionFields: Tenant changed, updating AI method')
        this.fetchAndSetTenantAiMethod(state.selectedTenantId)
      }
    })

    this.fetchTenants()
    this.fetchExtractionFields()
    document.addEventListener('click', this.handleClickAway)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
    // Unsubscribe from tenant selection store
    if (this.tenantStoreUnsubscribe) {
      this.tenantStoreUnsubscribe()
    }
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')

      this.readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)
      this.writePermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_WR' && p.is_active)
      this.readPermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_WR' && p.is_active)
    },
    handleClickAway(event) {
      const dropdownEl = this.$refs.dropdownEl
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.showPageSizeDropdown = false
        this.showEditCategoryDropdown = false
      }

      // Close tooltips when clicking outside
      this.hideTooltip()
      this.hideDropdownTooltip()
    },
    navigateToEdit(tenant) {
      const tenantName = localStorage?.getItem('url_name') || ''
      this.$router.push(`/${tenantName}/tenant/edit/${tenant.id}`)
    },
    async fetchTenants() {
      this.loadingStates.tenants = true
      try {
        const res = await get('/v1/tenants?page=1&pageSize=1000')
        if (res?.data?.code === 200) {
          this.tenants = (res.data.data.tenants || [])
            .filter((tenant) => tenant.is_active)
            .map((tenant) => ({
              label: tenant.tenant_name,
              value: tenant.id,
            }))
        }
      } catch (err) {
      } finally {
        this.loadingStates.tenants = false
      }
    },
    async fetchAndSetTenantAiMethod(tenantId) {
      try {
        const response = await get(`/v1/tenant-configs/tenant/${tenantId}`)
        if (response?.data?.code === 200) {
          const config = response.data.data
          let aiMethod = config.find((item) => item.key === 'ai_method')?.value
          if (aiMethod === 'Custom Comprehend - Async') {
            aiMethod = 'Custom Comprehend'
            this.aiMethodToDisplay = 'Custom Comprehend - Async'
          } else if (aiMethod === 'Custom Comprehend - Realtime') {
            aiMethod = 'Custom Comprehend'
            this.aiMethodToDisplay = 'Custom Comprehend - Realtime'
          }
          this.selectedAiMethod = aiMethod || ''
        }
      } catch (err) {}
    },

    async fetchTenantCategories(tenantId) {
      this.loadingStates.tenantCategories = true
      try {
        const response = await get(`/v1/tenant-categories?tenantId=${tenantId}&page=1&pageSize=100000&isActive=true`)
        if (response?.data?.code === 200) {
          this.categories = (response.data.data.data || [])
            .filter((category) => category.is_active)
            .map((category) => ({
              id: category.category_id,
              category_name: category.category_display_name,
            }))
        }
      } catch (err) {
      } finally {
        this.loadingStates.tenantCategories = false
      }
    },
    async fetchTenantDepartments(tenantId, categoryId) {
      this.loadingStates.departments = true
      try {
        const url = `/v1/tenant-departments?tenantId=${tenantId}&categoryId=${categoryId}&page=1&pageSize=100000`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.departments = (response.data.data.data || [])
            .filter((dept) => dept.is_active)
            .map((dept) => ({
              id: dept.department_id,
              department_name: dept.department_display_name,
            }))
        }
      } catch (err) {
        this.departments = []
      } finally {
        this.loadingStates.departments = false
      }
    },
    async fetchTenantDocumentTypes(tenantId, categoryId, departmentId) {
      this.loadingStates.documentTypes = true
      try {
        const url = `/v1/tenant-document-types?page=1&pageSize=10000&categoryId=${categoryId}&departmentId=${departmentId}&tenantId=${tenantId}`
        //  const url = `/v1/document-types?page=1&pageSize=10000&category_id=${categoryId}&department_id=${departmentId}`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.documentTypes = (response.data.data.data || [])
            .filter((doc) => doc.is_active)
            .map((doc) => ({
              id: doc.id,
              document_type_name: doc.document_type_display_name,
              vasion_form_id: doc.vasion_form_id,
              master_document_type_id: doc.document_type_id,
              model_id: doc.document_type.model_id,
              bda_blueprint: doc.bda_blueprint,
              bedrock_class_name: doc.bedrock_class_name,
            }))
        }
      } catch (err) {
      } finally {
        this.loadingStates.documentTypes = false
      }
    },
    getAiMethodFields() {
      return this.aiMethodFieldsMap[this.selectedAiMethod] || []
    },
    isSystemField(fieldName) {
      const systemFields = ['Status', 'Document Type', 'Index Date']
      return systemFields.includes(fieldName)
    },
    getComprehendFields(selectedDocType) {
      if (!selectedDocType.master_document_type_id || this.selectedAiMethod !== 'Default Textract') return
      this.aiMethodFieldsMap[this.selectedAiMethod] = [
        { id: 1, field_label: 'ABC', data_type: 'string' },
        { id: 2, field_label: 'EFG', data_type: 'date' },
      ]
    },

    async handleSyncAiMethodFields() {
      if (!this.selectedAiMethod || !this.selectedValues.documentTypeId) return

      try {
        let res
        let fields = []

        switch (this.selectedAiMethod) {
          case 'Default Textract':
            break

          case 'Custom Comprehend':
            res = await get(`/v1/documentFields/${this.selectedDocType.master_document_type_id}/${this.selectedDocType.model_id}`)
            fields = res.data.data
            break

          case 'Bedrock Data Automation':
            res = await post(`/v1/bda/getBluePrintDetails`, {
              blueprintArn: this.documentTypes?.find((doc) => doc.id === this.selectedDocType.id)?.bda_blueprint,
            })
            // alert('hiasdas')
            if (res?.data?.code === 201 && res.data.data && res.data.data.schema) {
              // Parse the schema JSON string
              const schema = JSON.parse(res.data.data.schema)

              // Extract property keys from the schema
              if (schema.properties) {
                const propertyKeys = Object.keys(schema.properties)

                // Convert property keys to the expected format
                fields = propertyKeys
              }
            }
            break

          default:
            showToast({
              title: this.$t('common.error'),
              message: this.$t('extractionFields.messages.error.unsupported_ai_method'),
              type: 'error',
            })
            return
        }

        // Store result
        this.aiMethodFieldsMap[this.selectedAiMethod] = fields
        showToast({
          title: this.$t('common.success'),
          message: this.$t('extractionFields.messages.success.ai_method_fields_synced'),
          type: 'success',
        })
      } catch (err) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.failed_to_fetch_ai_fields'),
          type: 'error',
        })
      }
    },

    async fetchVasionForms(tenantId) {
      this.loadingStates.vasionForms = true
      try {
        const response = await get(`/v1/vasion/objects?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          // this.vasionForms = response.data.data || []
          if (response.data.data && response.data.data.IndexForms) {
            this.vasionForms = Object.entries(response.data.data.IndexForms || []).map(([key, value]) => ({
              id: parseInt(key),
              vasion_form_id: value,
            }))
          } else {
            this.vasionForms = []
          }
        }
      } catch (err) {
      } finally {
        this.loadingStates.vasionForms = false
      }
    },
    async handleAiFieldSelect(field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }
      await this.handleExtractionFieldUpdate(
        field.id,
        {
          ...field,
          ai_method_field: field.bedrock_field,
          bedrock_field: field.bedrock_field,
        },
        'aiMethod'
      )
    },
    async handleDocumentFieldSelect(field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      try {
        const payload = field
        await this.handleExtractionFieldUpdate(field.id, payload, 'aiMethod')
      } catch (error) {
        showToast({
          title: this.$t('extractionFields.messages.error.update_failed'),
          message: this.$t('extractionFields.messages.error.field_update_error'),
          type: 'error',
        })
      }
    },

    async fetchExtractionFields() {
      if (this.selectedValues.tenantId && this.selectedValues.categoryId && this.selectedValues.departmentId && this.selectedValues.documentTypeId) {
        this.loadingStates.extractionFields = true
        try {
          const params = {
            page: this.page,
            pageSize: this.pageSize,
            ...(this.selectedValues.tenantId && {
              tenantId: this.selectedValues.tenantId,
            }),
            ...(this.selectedValues.categoryId && {
              categoryId: this.selectedValues.categoryId,
            }),
            ...(this.selectedValues.departmentId && {
              departmentId: this.selectedValues.departmentId,
            }),
            ...(this.selectedValues.documentTypeId && {
              tenantDocumentTypeId: this.selectedValues.documentTypeId,
            }),
            // ...(this.selectedValues.vasionFormId && {
            //   vasionFormId: this.selectedValues.vasionFormId,
            // }),
            ...(this.search && {
              vasionFieldName: this.search,
            }),
          }

          // Add filter params if filters exist
          if (this.filters) {
            Object.entries(this.filters).forEach(([key, value]) => {
              if (value !== '' && value !== null && value !== undefined) {
                // Convert boolean values to strings if needed
                params[key] = typeof value === 'boolean' ? value.toString() : value
              }
            })
          }

          const queryString = new URLSearchParams(params).toString()
          const url = `/v1/tenant-document-type-fields?${queryString}`

          const response = await get(url)
          if (response?.data?.code === 200) {
            this.extractionFields = response.data.data.data
            this.total = response.data.data.total
            this.totalPages = Math.ceil(this.total / this.pageSize)
          }
        } catch (err) {
        } finally {
          this.loadingStates.extractionFields = false
        }
      }
    },
    async handleFieldChange(fieldName, newValue) {
      if (fieldName === 'tenantId') {
        // Reset dependent fields
        this.selectedValues = {
          ...this.selectedValues,
          categoryId: '',
          departmentId: '',
          documentTypeId: '',
          vasionFormId: '',
        }
        this.categories = []
        this.departments = []
        this.documentTypes = []
        this.vasionForms = []
        this.selectedAiMethod = ''

        if (newValue) {
          await Promise.all([this.fetchTenantCategories(newValue), this.fetchVasionForms(newValue), this.fetchAndSetTenantAiMethod(newValue)])
        }
      }

      if (fieldName === 'categoryId') {
        // Reset dependent fields
        this.selectedValues = {
          ...this.selectedValues,
          departmentId: '',
          documentTypeId: '',
        }
        this.departments = []
        this.documentTypes = []

        if (newValue && this.selectedValues.tenantId) {
          await this.fetchTenantDepartments(this.selectedValues.tenantId, newValue)
        }
      }

      if (fieldName === 'departmentId') {
        // Reset document type
        this.selectedValues = {
          ...this.selectedValues,
          documentTypeId: '',
        }
        this.documentTypes = []

        if (newValue && this.selectedValues.tenantId && this.selectedValues.categoryId) {
          await this.fetchTenantDocumentTypes(this.selectedValues.tenantId, this.selectedValues.categoryId, newValue)
        }
      }

      // Fetch extraction fields after any dropdown change
      await this.fetchExtractionFields()
    },
    async handleSyncNewFields() {
      this.loadingStates.syncVasionFields = true
      try {
        const response = await post(`/v1/tenant-document-type-fields/sync-vasion-fields?tenantId=${this.selectedValues.tenantId}&tenantDocumentTypeId=${this.selectedValues.documentTypeId}`)
        if (response?.data?.code === 200) {
          this.fetchExtractionFields()
        }
      } catch (err) {
      } finally {
        this.loadingStates.syncVasionFields = false
      }
      // this.showAddForm = true
    },
    handleConfidenceChange(event, field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }
      const value = event.target.value
      // Allow only numbers and one decimal point
      if (/^\d*\.?\d*$/.test(value)) {
        // Convert to number and validate range (0-100)
        const numValue = parseFloat(value)
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
          field.ml_confidence_level = numValue
          this.handleExtractionFieldUpdate(field.id, field, 'confidence')
        }
      }
    },
    async handleExtractionFieldUpdate(id, payload, type) {
      this.loadingStates.updateExtractionField = true
      try {
        let response = await put(`/v1/tenant-document-type-fields/${id}`, payload)
        if (response?.data?.code === 200) {
          showToast({
            title: this.$t('common.success'),
            message: type === 'confidence' ? this.$t('extractionFields.messages.success.confidence_level_updated') : type === 'status' ? (payload.is_active ? this.$t('extractionFields.messages.success.field_enabled') : this.$t('extractionFields.messages.success.field_disabled')) : this.$t('extractionFields.messages.success.extraction_field_updated'),
            type: 'success',
          })
        } else {
          showToast({
            title: this.$t('common.error'),
            message: this.$t('extractionFields.messages.error.failed_to_update_extraction_field'),
            type: 'error',
          })
        }
      } catch (err) {
        showToast({
          title: this.$t('extractionFields.messages.error.update_error'),
          message: err.message || this.$t('extractionFields.messages.error.failed_to_update_extraction_field'),
          type: 'error',
        })
      } finally {
        this.loadingStates.updateExtractionField = false
      }
    },
    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },
    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchExtractionFields()
    },
    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchExtractionFields()
      }
    },
    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchExtractionFields()
      }
    },
    handleFilterChange(filters) {
      this.filters = filters
      this.pageSize = 10
      this.page = 1
      this.fetchExtractionFields()
      // Implement filter logic here
    },
    handleStatusToggle(field, newValue) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }
      this.pendingStatusChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModal = true
    },
    handleDefaultCheck(field, newValue) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }
      this.pendingStatusChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModalForDefault = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
      this.showConfirmModalForDefault = false
      this.pendingStatusChange = null
    },
    async confirmStatusChange() {
      if (!this.pendingStatusChange) return

      const { field, newStatus } = this.pendingStatusChange

      const updatedField = this.extractionFields.find((f) => f.id === field.id)

      if (updatedField) {
        updatedField.is_active = newStatus
        await this.handleExtractionFieldUpdate(
          field.id,
          {
            ...field,
            is_active: newStatus,
          },
          'status'
        )
      }

      this.closeConfirmModal()
    },
    async confirmDefaultValueChange() {
      if (!this.pendingStatusChange) return

      const { field, newStatus } = this.pendingStatusChange
      const updatedField = this.extractionFields.find((f) => f.id === field.id)

      if (updatedField) {
        updatedField.default_value_required = newStatus
        await this.handleExtractionFieldUpdate(
          field.id,
          {
            ...field,
            default_value_required: newStatus,
          },
          'default_value_required'
        )
      }
      this.closeConfirmModal()
    },
    handleSearch() {
      // Clear existing timeout
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }

      // Set new timeout
      this.debounceTimeout = setTimeout(() => {
        this.page = 1 // Reset to first page when searching
        this.fetchExtractionFields()
      }, 300) // 300ms debounce
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    showTooltip(event, text) {
      if (!text || text.length <= 20) return

      this.tooltipText = text
      this.tooltipVisible = true

      // Position tooltip relative to mouse
      const rect = event.target.getBoundingClientRect()
      this.tooltipStyle = {
        left: `${rect.left}px`,
        top: `${rect.bottom + 5}px`,
        position: 'fixed',
        zIndex: 9999,
      }
    },

    hideTooltip() {
      this.tooltipVisible = false
      this.tooltipText = ''
      this.tooltipStyle = {}
    },

    getFormattedDropdownValues(dropdownValues) {
      if (!dropdownValues) return ''
      try {
        const parsed = JSON.parse(dropdownValues)
        if (Array.isArray(parsed)) {
          return parsed.join(', ')
        }
        return String(dropdownValues)
      } catch (error) {
        return String(dropdownValues)
      }
    },

    showDropdownTooltip(event, dropdownValues) {
      if (!dropdownValues) return

      const formattedValues = this.getFormattedDropdownValues(dropdownValues)
      if (formattedValues.length <= 25) return

      this.dropdownTooltipText = formattedValues
      this.dropdownTooltipVisible = true

      // Position tooltip relative to mouse
      const rect = event.target.getBoundingClientRect()
      this.dropdownTooltipStyle = {
        left: `${rect.left}px`,
        top: `${rect.bottom + 5}px`,
        position: 'fixed',
        zIndex: 9999,
      }
    },

    hideDropdownTooltip() {
      this.dropdownTooltipVisible = false
      this.dropdownTooltipText = ''
      this.dropdownTooltipStyle = {}
    },

    openDropdownModal(field) {
      if (!field.vasion_drop_down_values) return

      try {
        const parsed = JSON.parse(field.vasion_drop_down_values)
        this.selectedDropdownValues = Array.isArray(parsed) ? parsed : [parsed]
      } catch (error) {
        this.selectedDropdownValues = [field.vasion_drop_down_values]
      }

      this.selectedFieldName = field.vasion_field_name
      this.showDropdownModal = true
    },

    closeDropdownModal() {
      this.showDropdownModal = false
      this.selectedDropdownValues = []
      this.selectedFieldName = ''
    },
  },
  watch: {
    'selectedValues.tenantId': {
      async handler(newValue, oldValue) {
        // Only react if this is a manual change (not from store subscription)
        if (newValue === oldValue || !newValue || this._isStoreUpdate) return

        console.log('ExtractionFields: tenantId watcher triggered:', newValue, 'from:', oldValue)

        this.handleFieldChange('tenantId', newValue)

        // Update the store with new tenant ID (will call API to check AI method)
        console.log('ExtractionFields: Calling store.setSelectedTenantId with:', newValue)
        await tenantSelectionStore.setSelectedTenantId(newValue)
      },
      immediate: true,
    },
    'selectedValues.categoryId': {
      handler(newValue) {
        this.handleFieldChange('categoryId', newValue)
      },
    },
    'selectedValues.departmentId': {
      handler(newValue) {
        this.handleFieldChange('departmentId', newValue)
      },
    },
    'selectedValues.documentTypeId': {
      handler(newValue) {
        const selectedDocType = this.documentTypes.find((doc) => doc.id === newValue)
        if (selectedDocType) {
          this.selectedValues.vasionFormId = selectedDocType.vasion_form_id
          this.selectedDocType = selectedDocType
          this.handleSyncAiMethodFields()
        }
        this.fetchExtractionFields()
      },
    },
    'selectedValues.vasionFormId': {
      handler() {
        this.fetchExtractionFields()
      },
    },
  },
}
</script>

<style scoped>
.confidence-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  font-family: Archivo;
  font-size: 14px;
  text-align: right;
}

.confidence-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.confidence-input::-webkit-outer-spin-button,
.confidence-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Vasion Field Key Tooltip Styles */
.vasion-field-key-container {
  position: relative;
  display: inline-block;
}

.vasion-field-key-text {
  display: inline-block;
  max-width: 100%;
}

.vasion-field-key-truncated {
  cursor: pointer;
  /* color: #007bff; */
  text-decoration: underline;
}

.vasion-field-key-tooltip {
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 300px;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

/* Dropdown Values Tooltip Styles */
.dropdown-values-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dropdown-values-text {
  display: inline-block;
  max-width: 100%;
}

.dropdown-values-truncated {
  cursor: pointer;
  text-decoration: underline;
}

.dropdown-values-tooltip {
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 300px;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.dropdown-view-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.dropdown-view-icon:hover {
  background-color: #f0f9fd;
}

.dropdown-view-icon svg {
  transition: transform 0.2s ease;
}

.dropdown-view-icon:hover svg {
  transform: scale(1.1);
}

.searchBar {
  display: flex;
  align-items: center;
  width: 386px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dadada;
  background: #fff;
  padding: 0 10px;
  gap: 10px;
}

.searchInput {
  border: none;
  outline: none;
  width: 100%;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #8f969b;
}

.button-group {
  margin-top: 20px;
  display: flex;
  gap: 20px;
}

.cancel-btn {
  padding: 10px 32px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-family: Archivo;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
}

.save-btn {
  padding: 10px 32px;
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.form-field input,
.form-field select {
  width: 100%;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 28px;
  font-weight: 500;
  line-height: 150%;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageSizeDropdownNew li:hover {
  background: #f0f0f0;
}

.custom-extraction-field-top-bar {
  margin-bottom: 20px;
}

.extraction-top-bar-title {
  color: var(--primary-color);
  text-align: center;
  font-family: Archivo;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.dropdown-container {
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
}

.dropdown-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 25px;
  width: 100%;
}

.form-field {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.form-field label {
  margin-bottom: 6px;
  font-family: Archivo;
  font-size: 14px;
  color: #999999;
  white-space: nowrap;
  /* Prevents wrapping */
  overflow: hidden;
  /* Hides overflow */
  text-overflow: ellipsis;
  /* Shows ellipsis */
  display: block;
  /* Required for ellipsis to work */
}

.custom-select {
  width: 100%;
  min-width: 0;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
  background-color: white;
  cursor: pointer;
  text-overflow: ellipsis;
}

.custom-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

.extraction-top-bar-title {
  margin-bottom: 20px;
}

.sync-icon-wrapper {
  cursor: pointer;
  display: flex;
  margin-top: 16px;
  justify-content: end;
  align-items: end;
}

.sync-icon {
  font-size: 24px;
  cursor: pointer;
  color: var(--primary-color);
}

.sync-icon-header {
  font-size: 16px;
  cursor: pointer;
  color: var(--primary-color);
}

.sync-icon:hover {
  color: #0d6efd;
  /* Optional hover color */
}
</style>
